import { Injectable } from '@angular/core';
import { CheckoutState } from '@core/enums/cart/cart-checkout-state.enum';
import { CheckoutStatus } from '@core/enums/cart/cart-checkout-status.enum';
import { ICartCheckoutStatus } from '@core/interfaces/cart/cart-checkout-status.interface';
import { ICartSource } from '@core/interfaces/cart/cart-source.interface';
import { ICart } from '@core/interfaces/cart/cart.interface';
import { IDiscount } from '@core/interfaces/cart/discount.interface';
import { CartCheckoutStatus } from '@core/models/cart/cart-checkout-status.model';
import { CartItemViewModel } from '@core/models/cart/cart-item-view-model.model';
import { CartItem } from '@core/models/cart/cart-item.model';
import { Cart } from '@core/models/cart/cart.model';
import { Discount } from '@core/models/cart/discount.model';
import { SelectEventRequest } from '@core/models/cart/select-event-request.model';
import { CartStoreService } from '@core/services/cart/cart-store.service';
import { AAGUID, AppError, Utils } from '@interticket/core';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, concatMap, finalize, map, mapTo, switchMap, take, tap } from 'rxjs/operators';
import { DiscountType } from '../../enums/cart/discount-type.enum';
import { EventsService } from '../event/events.service';

@Injectable()
export class CartService {

  readonly onError$ = new Subject<AppError>();
  readonly isDisabled$ = new BehaviorSubject(false);

  constructor(
    private cartSourceService: ICartSource,
    private cartStoreService: CartStoreService,
    private eventService: EventsService
  ) { }

  private aggregateCartItems(cartItems: CartItem[], isSummary: boolean): void {
    const cartItemsAggregated = cartItems.map(item => this.aggregateCartItem(item));

    if (isSummary) {
      this.cartStoreService.cartSummaryItems = cartItemsAggregated;
    } else {
      this.cartStoreService.cartItems = cartItemsAggregated;
    }
  }

  private aggregateCartItem(item: CartItem): CartItemViewModel {
    const event = this.cartStoreService.cartEvents.find(val => val.id === item.eventId);
    const priceCategory = event.priceCategories.find(val => val.id === item.priceCategoryId);

    return new CartItemViewModel({
      ...priceCategory,
      ...item,
      event,
    });
  }

  private getSelectionIds(id: string, selectBy: keyof CartItem) {
    return this.cartStoreService.cartItems
      .filter((cartItem: CartItem) => cartItem[selectBy] === id)
      .map((cartItem: CartItem) => cartItem.selectionId);
  }

  private aggregateCart(cart$: Observable<ICart>, isSummary?: boolean): Observable<Cart> {
    return cart$.pipe(
      take(1),
      map((iCart: ICart) => iCart.id ? new Cart(iCart) : null),
      tap((cart: Cart) => {
        if (isSummary) {
          return;
        }

        this.cartStoreService.setCart(cart);
      }),
      // SYNC EVENTS
      switchMap(cart => {
        if (!cart?.cartItems?.length) {
          this.cartStoreService.cartEvents = [];
          return of(cart);
        }

        const eventIds = cart.cartItems
          .map((cartItem: CartItem) => cartItem.eventId)
          .filter((eventId: AAGUID, index: number, Ids: AAGUID[]) => Ids.indexOf(eventId) === index);

        return this.eventService.getListByIds(eventIds)
          .pipe(
            map(events => {
              this.cartStoreService.cartEvents = events;
              return cart;
            })
          );
      }),
      tap((cart) => cart && this.aggregateCartItems(cart.cartItems, isSummary))
    );
  }

  handleRequest<T>(observable: Observable<T>): Observable<T> {
    this.isDisabled$.next(true);

    return observable.pipe(
      finalize(() => this.isDisabled$.next(false)),
      catchError(e => {
        this.onError$.next(e);
        return throwError(e);
      })
    );
  }

  getCart(): Observable<Cart> {
    return this.aggregateCart(this.cartSourceService.getCart());
  }

  addToCart(selectEventRequest: SelectEventRequest): Observable<Cart> {
    return this.cartSourceService.addToCart(selectEventRequest)
      .pipe(
        concatMap(() => this.getCart())
      );
  }

  getCartById(cartId: AAGUID): Observable<Cart> {
    return this.aggregateCart(this.cartSourceService.getCartById(cartId), true);
  }

  deleteCart(): Observable<any> {
    return this.cartSourceService.deleteCart(this.cartStoreService.cart.id)
      .pipe(
        tap(() => this.cartStoreService.setCart(null))
      );
  }

  deleteFromCart(priceCategoryId: AAGUID, amount = 1): Observable<Cart> {
    const selectionIds: AAGUID[] = this.getSelectionIds(priceCategoryId, 'priceCategoryId').slice(-amount);

    return this.cartSourceService.deleteFromCart(selectionIds)
      .pipe(
        concatMap(() => this.getCart())
      );
  }

  deleteGiftCardsToBuyFromCart(giftCardIds: AAGUID[]): Observable<Cart> {
    return this.cartSourceService.deleteGiftCardsToBuyFromCart(giftCardIds)
      .pipe(
        concatMap(() => this.getCart())
      );
  }

  deleteCartItems(selectionIds: AAGUID[]): Observable<Cart> {
    return this.cartSourceService.deleteFromCart(selectionIds)
      .pipe(
        concatMap(() => this.getCart())
      );
  }

  deleteGiftCard(couponId: AAGUID): Observable<Cart> {
    return this.cartSourceService.deleteGiftCard(couponId)
      .pipe(
        concatMap(() => this.getCart())
      );
  }

  checkoutCart(paymentMethodId: AAGUID): Observable<Response> {
    return this.cartSourceService.checkoutCart(paymentMethodId)
      .pipe(take(1));
  }

  getCheckoutStatus(): Observable<CartCheckoutStatus> {
    return this.cartSourceService.getCheckoutStatus()
      .pipe(
        take(1),
        map((iCheckoutStatus: ICartCheckoutStatus) => new CartCheckoutStatus(iCheckoutStatus))
      );
  }

  pollCheckoutStatus(afterPay = false): Observable<CartCheckoutStatus> {
    return Utils.pollTakeWhile(
      () => this.getCheckoutStatus(),
      (checkoutStatus: CartCheckoutStatus) => checkoutStatus.status === CheckoutStatus.IN_PROGRESS &&
        (checkoutStatus.state !== CheckoutState.PAYMENT_TRANSACTION_STARTED || afterPay)
    );
  }

  getDiscount(couponId: string): Observable<Discount | null> {
    return this.cartSourceService.getDiscount(couponId)
      .pipe(
        take(1),
        map((iDiscount: IDiscount | null) => {
          const discount = iDiscount ? new Discount(iDiscount) : null;
          return discount;
        }),
        switchMap((discount: Discount | null) => {
          if (discount?.discountType === DiscountType.GIFTCARD && !discount?.errorMessage) {
            return this.getCart()
              .pipe(
                mapTo(discount),
              );
          }

          return of(discount);
        }),
      );
  }

  applyDiscounts(cartItemIds: AAGUID[], couponId: AAGUID): Observable<Cart> {
    return this.cartSourceService.applyDiscounts(cartItemIds, couponId)
      .pipe(
        concatMap(() => this.getCart())
      );
  }

  removeDiscount(cartItemId: AAGUID): Observable<Cart> {
    return this.cartSourceService.removeDiscount(cartItemId)
      .pipe(
        concatMap(() => this.getCart())
      );
  }

}
